import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

// SEND-301g (Howard ruled 2026-08-14) — **THIS SCREEN ONLY PICKS THE
// DRAWINGS.**
//   "On EST-861655 the FRONT and BACK boxes on the sheet are an extra step. A
//    contractor does not need them. This screen sorts the pages and opens the
//    walk. That is all. Remove the label boxes from the sheet screen. Remove
//    the rename row under the sheet. DO NOT MAKE HIM DRAW A BOX BEFORE THE
//    WALK OPENS. He picks elevation, floor plan, or skip. Open the walk on
//    this page opens the full photo walk on that page. HE TAGS THE WALL IN THE
//    WALK, the same FRONT / LEFT / BACK / RIGHT chips Measure from Photos
//    already has."
//
// So this screen holds a page, a way to the next one, and the one button that
// opens the walk. NO box, NO rename row, NO scale, NO step, NO chip, NO
// quantity — the walk owns every one of those and this screen owns none.
const UPLOADS = `${process.env.REACT_APP_BACKEND_URL}/api/uploads`;

export default function BlueprintWalk({ estId, set, onSet, onOpenPage }) {
  const pages = useMemo(() => set?.walk_pages || [], [set]);
  const [at, setAt] = useState(0);
  const [busy, setBusy] = useState("");
  const page = pages[Math.min(at, Math.max(pages.length - 1, 0))];
  const lock = set?.source_lock || null;

  // THE PAGE IS THE PHOTO: this asks the door for the page's own photo key and
  // HANDS IT UP, and the tile opens THE SAME FULL PHOTO SCREEN AS MEASURE FROM
  // PHOTOS on it. Nothing is cut out of the sheet.
  const openTheWalk = async () => {
    setBusy("open");
    try {
      const res = await api.post(
        `/estimates/${estId}/blueprint-draw/pages/${page.page_num}/open`);
      onSet(res.data);
      onOpenPage(res.data.photo_key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "that page did not open");
    } finally {
      setBusy("");
    }
  };

  if (!page) {
    return (
      <p className="text-[11px] text-[var(--muted)]"
        data-testid="blueprint-walk-no-pages">
        No page is kept for the walk — accept a list with at least one
        elevation or floor plan.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="blueprint-walk">
      {lock?.locked ? (
        <p className="flex items-start gap-1.5 text-[11px] text-[var(--danger,#dc2626)] border border-[var(--danger,#dc2626)] p-2"
          data-testid="blueprint-walk-source-lock">
          <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {lock.why}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <Button size="sm" variant="outline" disabled={at === 0}
          onClick={() => setAt(at - 1)}
          data-testid="blueprint-walk-prev">
          <ChevronLeft className="w-3 h-3" />
        </Button>
        <div className="text-[11px] font-bold uppercase tracking-wider text-center"
          data-testid="blueprint-walk-at">
          Page {page.page_num} · {page.title || "no title on this page"} ·{" "}
          {page.is_siding_face ? "elevation" : "floor plan"} · {at + 1} of{" "}
          {pages.length}
        </div>
        <Button size="sm" variant="outline" disabled={at >= pages.length - 1}
          onClick={() => setAt(at + 1)}
          data-testid="blueprint-walk-next">
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!!busy || lock?.locked}
          onClick={openTheWalk} data-testid="blueprint-walk-open-the-walk">
          <Ruler className="w-3 h-3 mr-1.5" />
          {page.photo_key ? "Back into the walk on this page"
            : "Open the walk on this page"}
        </Button>
        <span className="text-[10px] text-[var(--muted)]"
          data-testid="blueprint-walk-same-walk-note">
          The whole page opens in the photo walk — the same scale, the same
          steps, the same chips, the same Apply All. You tag the wall in the
          walk. Nothing is cut out of the sheet.
        </span>
      </div>

      {!page.is_siding_face ? (
        <p className="text-[11px] text-[var(--ink-2)] border border-[var(--border)] p-2"
          data-testid="blueprint-walk-floor-plan-notice">
          A floor plan is its own sheet, not a siding face.
        </p>
      ) : null}

      <div className="border border-[var(--border)] bg-white"
        data-testid={`blueprint-walk-canvas-${page.page_num}`}>
        {page.thumb ? (
          <img src={`${UPLOADS}/${page.thumb}`} alt={`page ${page.page_num}`}
            draggable={false}
            className="w-full max-h-[52vh] object-contain"
            data-testid={`blueprint-walk-sheet-${page.page_num}`} />
        ) : (
          <div className="h-48 grid place-items-center text-[10px] text-[var(--muted)]">
            no image of this sheet
          </div>
        )}
      </div>
    </div>
  );
}
